<template>
  <div>
    <CRow>
      <CCol lg="12">
        <h1 class="main-header" v-translate translate-context="tag_definitions">Tag Definitions</h1>
        <p class="text-muted mb-4" v-translate translate-context="tag_definitions.subtitle">
          Configure tags for categorizing and organizing photos, faces, people, clusters, and check-ins
        </p>

        <CCard>
          <CCardHeader>
            <div class="card-header-actions">
              <CButton color="primary" @click="onCreate" v-translate translate-context="tag_definitions">
                Create Tag Definition
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody class="px-0">
            <AjaxTable
                :fields="fields"
                :data="tagDefinitions"
                :fetch="fetchList"
                ref="ajaxTable"
            >
              <template #name="{item}">
                <td>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-muted small">{{ item.code }}</div>
                </td>
              </template>

              <template #level="{item}">
                <td>
                  <CBadge :color="getLevelColor(item.level)">
                    {{ getLevelLabel(item.level) }}
                  </CBadge>
                  <span v-if="item.is_contract_level"
                        class="ml-1"
                        :title="$pgettext('tag_definitions.tooltip', 'This tag is shared across all events in the contract')"
                        style="cursor: help;">
                    ⚠️
                  </span>
                </td>
              </template>

              <template #is_enabled="{item}">
                <td>
                  <CBadge :color="item.is_enabled ? 'success' : 'secondary'">
                    <span v-if="item.is_enabled" v-translate translate-context="tag_definitions">Enabled</span>
                    <span v-else v-translate translate-context="tag_definitions">Disabled</span>
                  </CBadge>
                </td>
              </template>

              <template #scopes="{item}">
                <td>
                  <CBadge v-for="scope in getScopes(item)" :key="scope" color="primary" class="mr-1 mb-1">
                    {{ scope }}
                  </CBadge>
                </td>
              </template>

              <template #actions="{item}">
                <td class="text-right">
                  <template v-if="!canEditTag(item)">
                    <CBadge :color="item.is_system ? 'secondary' : 'purple'">
                      <span v-if="item.is_system" v-translate translate-context="tag_definitions">System</span>
                      <span v-else-if="item.is_contract_level" v-translate translate-context="tag_definitions">Contract</span>
                    </CBadge>
                  </template>
                  <template v-else>
                    <CButton
                        variant="outline"
                        color="primary"
                        size="sm"
                        class="mr-2"
                        @click="onEdit(item.id)"
                        v-translate translate-context="tag_definitions"
                    >
                      Edit
                    </CButton>
                    <CButton
                        variant="outline"
                        color="danger"
                        size="sm"
                        @click="onDelete(item)"
                        v-if="canDeleteTag(item)"
                        v-translate translate-context="tag_definitions"
                    >
                      Delete
                    </CButton>
                  </template>
                </td>
              </template>
            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import AjaxTable from "@/domain/core/components/AjaxTable.vue";
import {getTagScopeLabel} from "@/domain/core/constant/tagScope";
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: 'TagDefinitionsList',
  components: {AjaxTable},
  data() {
    return {}
  },
  async mounted() {
    await this.fetchConfig({type: 'tag_definition', id: this.picaServiceId, scope: ConfigScope.PICA_SERVICE})
  },
  computed: {
    ...mapGetters('tagDefinitions', [
      'tagDefinitions',
    ]),
    picaServiceId() {
      return this.$route.params.picaServiceId
    },
    booleanFilterOptions() {
      return [
        {value: null, label: this.$pgettext('tag_definitions.filter', 'Any')},
        {value: 'true', label: this.$pgettext('tag_definitions.filter', 'Yes')},
        {value: 'false', label: this.$pgettext('tag_definitions.filter', 'No')}
      ]
    },
    scopeFilterOptions() {
      return [
        {value: null, label: this.$pgettext('tag_definitions.filter', 'Any')},
        {value: 'checkin', label: this.$pgettext('tag_definitions.scope', 'Check-in')},
        {value: 'media', label: this.$pgettext('tag_definitions.scope', 'Media')},
        {value: 'face', label: this.$pgettext('tag_definitions.scope', 'Face')},
        {value: 'person', label: this.$pgettext('tag_definitions.scope', 'Person')},
        {value: 'cluster', label: this.$pgettext('tag_definitions.scope', 'Cluster')}
      ]
    },
    levelFilterOptions() {
      return [
        {value: null, label: this.$pgettext('tag_definitions.filter', 'Any')},
        {value: 'system', label: this.$pgettext('tag_definitions.level', 'System')},
        {value: 'contract', label: this.$pgettext('tag_definitions.level', 'Contract')},
        {value: 'service', label: this.$pgettext('tag_definitions.level', 'Service')}
      ]
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$pgettext('tag_definitions.table', 'Name'),
          _style: 'width: 20%;'
        },
        {
          key: 'level',
          label: this.$pgettext('tag_definitions.table', 'Level'),
          _style: 'width: 12%;',
          sorter: true,
          filter: {
            type: 'select',
            values: this.levelFilterOptions
          }
        },
        {
          key: 'is_enabled',
          label: this.$pgettext('tag_definitions.table', 'Enabled'),
          _style: 'width: 10%;',
          sorter: false,
          filter: {
            type: 'select',
            values: this.booleanFilterOptions
          }
        },
        {
          key: 'scopes',
          label: this.$pgettext('tag_definitions.table', 'Applicable To'),
          _style: 'width: 20%;',
          sorter: false,
          filter: {
            type: 'select',
            values: this.scopeFilterOptions
          }
        },
        {
          key: 'actions',
          label: this.$pgettext('tag_definitions.table', 'Actions'),
          _style: 'width: 25%;',
          filter: false,
          sorter: false
        }
      ]
    }
  },
  methods: {
    ...mapActions('tagDefinitions', [
      'fetchTagDefinitions',
      'deleteTagDefinition'
    ]),
    ...mapActions('login', [
      'fetchConfig',
    ]),
    async fetchList({filters, pagination, sorting}) {
      await this.fetchTagDefinitions({
        picaServiceId: this.picaServiceId,
        filters,
        pagination,
        sorting
      })
    },
    onCreate() {
      this.$router.push({
        name: 'tagDefinitions.create',
        params: {picaServiceId: this.picaServiceId}
      })
    },
    onEdit(id) {
      this.$router.push({
        name: 'tagDefinitions.edit',
        params: {picaServiceId: this.picaServiceId, id}
      })
    },
    async onDelete(item) {
      const confirmed = await this.confirm(
          this.$pgettext('tag_definitions.confirm', 'Are you sure you want to delete this tag definition?'),
          this.$pgettext('tag_definitions.confirm', 'Delete "%{name}"').replace('%{name}', item.name)
      )
      if (!confirmed) return

      await this.deleteTagDefinition({
        picaServiceId: this.picaServiceId,
        id: item.id
      })
      this.$refs.ajaxTable.fetchList()
    },
    getScopes(item) {
      if (!item.scopes || !Array.isArray(item.scopes)) return []
      return item.scopes.map(scopeKey => getTagScopeLabel(scopeKey))
    },
    getLevelLabel(level) {
      const labels = {
        'system': this.$pgettext('tag_definitions.level', 'System'),
        'contract': this.$pgettext('tag_definitions.level', 'Contract'),
        'service': this.$pgettext('tag_definitions.level', 'Service')
      }
      return labels[level] || level
    },
    getLevelColor(level) {
      const colors = {
        'system': 'info',      // blu
        'contract': 'purple',  // viola
        'service': 'success'   // verde
      }
      return colors[level] || 'secondary'
    },
    canEditTag(tag) {
      // System tags: mai editabili
      if (tag.is_system) return false

      // Contract tags: editabili solo con permesso
      if (tag.is_contract_level) {
        return this.hasPerm('tag_definition.manage_contract_tags')
      }

      // Service tags: sempre editabili
      return true
    },
    canDeleteTag(tag) {
      // Usa il campo is_deletable e verifica che sia editabile
      return tag.is_deletable && this.canEditTag(tag)
    }
  }
}
</script>

<style scoped>
/* Custom badge color for contract level */
::v-deep .badge.badge-purple {
  background-color: #7b1fa2;
  color: white;
}
</style>
